/**
 * KillSwitchStatus — live "is trading halted?" readout for the operator.
 *
 * Reads the authoritative trading circuit-breaker state from
 * GET /api/trading/kill-switch (non-mutating). It lets the operator SEE whether
 * the kill switch is engaged, not just fire it.
 *
 * Fail-safe: the dangerous direction for a halt readout is a FALSE-HALTED
 * (showing "stopped" while the bot is actually trading → false calm). So any
 * unreadable/errored state shows UNKNOWN — never a definitive HALTED or ACTIVE.
 * Only an authoritative `trading_blocked` (with no error flag) decides.
 */
import { useQuery } from "@tanstack/react-query";

import { apiGet } from "@/lib/api-client";
import { cn } from "@/lib/cn";

export interface KillSwitchStatusResponse {
  trading_blocked?: boolean;
  error?: string;
}

export type HaltDisplay = "HALTED" | "ACTIVE" | "UNKNOWN" | "LOADING";

interface ResolveArgs {
  data?: KillSwitchStatusResponse | null;
  isError: boolean;
  isLoading: boolean;
}

export function resolveHaltDisplay({ data, isError, isLoading }: ResolveArgs): HaltDisplay {
  if (isError) return "UNKNOWN";
  if (!data) return isLoading ? "LOADING" : "UNKNOWN";
  // The backend could not read the breaker — do not trust trading_blocked.
  if (data.error) return "UNKNOWN";
  if (data.trading_blocked === true) return "HALTED";
  if (data.trading_blocked === false) return "ACTIVE";
  // Present but no usable signal -> do not guess.
  return "UNKNOWN";
}

export function useKillSwitchStatus() {
  return useQuery({
    queryKey: ["trading", "kill-switch"],
    queryFn: () => apiGet<KillSwitchStatusResponse>("/api/trading/kill-switch"),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

const HALT_STYLES: Record<
  HaltDisplay,
  { label: string; className: string; dotClassName: string; title: string }
> = {
  HALTED: {
    label: "TRADING HALTED",
    className: "border-red-500/60 bg-red-500/20 text-red-200",
    dotClassName: "bg-red-400 animate-pulse",
    title: "Kill switch ENGAGED — trading is halted",
  },
  ACTIVE: {
    label: "Trading active",
    className: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    dotClassName: "bg-emerald-400",
    title: "Kill switch not engaged — trading is active",
  },
  UNKNOWN: {
    label: "Halt unknown",
    className: "border-amber-500/50 bg-amber-500/10 text-amber-300",
    dotClassName: "bg-amber-400",
    title: "Could not read the kill-switch state — treat as unconfirmed",
  },
  LOADING: {
    label: "…",
    className: "border-border bg-transparent text-muted",
    dotClassName: "bg-muted/50",
    title: "Reading kill-switch state…",
  },
};

export function KillSwitchStatus({ className }: { className?: string }) {
  const { data, isError, isLoading } = useKillSwitchStatus();
  const display = resolveHaltDisplay({ data, isError, isLoading });
  const style = HALT_STYLES[display];

  return (
    <span
      role="status"
      aria-label={style.title}
      title={style.title}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold tracking-wide",
        style.className,
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", style.dotClassName)} />
      {style.label}
    </span>
  );
}
